"use client";

import {
  fetchServerVersion,
  getBuildId,
  getBuildVersion,
} from "@/lib/app-version";
import { useEffect, useState } from "react";

export function PatchNotesTab() {
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [serverBuildId, setServerBuildId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const buildVersion = getBuildVersion();
  const buildId = getBuildId();

  useEffect(() => {
    const fetchVersion = async () => {
      setIsLoading(true);
      const info = await fetchServerVersion();
      if (info) {
        setServerVersion(info.version);
        setServerBuildId(info.buildId);
      }
      setIsLoading(false);
    };

    fetchVersion();
  }, []);

  const hasUpdate = serverBuildId && serverBuildId !== buildId;

  return (
    <div className="space-y-4 p-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">
          Version Info
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Current build and server version
        </p>
      </div>

      {/* Current Build Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="text-xs font-medium text-blue-700 mb-2">
          Running Build
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-blue-700">Version</span>
          <span className="text-sm font-mono font-bold text-blue-900">
            {buildVersion}
          </span>
        </div>
        <div className="flex justify-between items-center mt-1">
          <span className="text-xs text-blue-700">Build ID</span>
          <span className="text-xs font-mono text-blue-800">{buildId}</span>
        </div>
      </div>

      {/* Server Version Info */}
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600" />
          <span className="ml-2 text-sm text-gray-600">Checking server...</span>
        </div>
      ) : (
        <div
          className={`border rounded-lg p-3 ${
            hasUpdate
              ? "bg-amber-50 border-amber-200"
              : "bg-green-50 border-green-200"
          }`}
        >
          <div
            className={`text-xs font-medium mb-2 ${
              hasUpdate ? "text-amber-700" : "text-green-700"
            }`}
          >
            Server Version
          </div>
          <div className="flex justify-between items-center">
            <span
              className={`text-xs ${hasUpdate ? "text-amber-700" : "text-green-700"}`}
            >
              Version
            </span>
            <span
              className={`text-sm font-mono font-bold ${
                hasUpdate ? "text-amber-900" : "text-green-900"
              }`}
            >
              {serverVersion || "Unknown"}
            </span>
          </div>
          <div className="flex justify-between items-center mt-1">
            <span
              className={`text-xs ${hasUpdate ? "text-amber-700" : "text-green-700"}`}
            >
              Build ID
            </span>
            <span
              className={`text-xs font-mono ${
                hasUpdate ? "text-amber-800" : "text-green-800"
              }`}
            >
              {serverBuildId || "Unknown"}
            </span>
          </div>
          {hasUpdate && (
            <div className="mt-2 pt-2 border-t border-amber-200">
              <span className="text-xs text-amber-800 font-medium">
                Update available - refresh to get latest version
              </span>
            </div>
          )}
        </div>
      )}

      <div className="pt-3 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          The kiosk will auto-refresh when an update is detected during idle.
        </p>
      </div>
    </div>
  );
}
